import { Color } from '@material-ui/lab/Alert';
import { IPersonalIdentificationFields } from 'features/files/parties/interfaces/PersonalInformation';
import { ReactNode } from 'react';
import { TransactionStatusEnum } from 'utilities/verification/types';

export interface FileOrderInsightsType {
  fileId: string;
  fileNumber: string;
  isGenerating: boolean;
  lastGeneratedDate?: string;
  hasError: boolean;
  parties: Party[];
  properties: Property[];
  jackets: OrderInsightJacket[];
}
export interface Party {
  id: number;
  partyType: string;
  filePartyType: 'Buyer' | 'Seller' | 'Lender';
  partyName: string;
  isSpouse: boolean;
  address: Address;
  firstName?: string;
  middleName?: string;
  lastName?: string;
  risks?: Risk[];
  expanded?: boolean;
  phoneNumber?: string;
  emailAddress?: string;
  contactValidation?: ContactValidationResult;
  enhancedIdentifyVerification?: IdentityVerificationResult;
  docType?: string;
  language?: string;
  key: string;
  fileNumber?: string;
  underwriterCode?: string;
  suffix?: string;
  personalInformation?: IPersonalIdentificationFields;
  fileId?: string;
}

export interface ISavePhoneNumberPayload {
  fileId: string;
  firmId: string;
  partyId: number;
  partyType: string;
  party: 'Buyer' | 'Seller' | 'Lender';
  isSpouse: boolean;
  address: Address;
  phone?: string;
  email?: string;
}

export type ContactValidationSubmittedData =
  | ({ phoneNumber: string; emailAddress?: string } & ContactValidationOptionalFields)
  | ({ emailAddress: string; phoneNumber?: string } & ContactValidationOptionalFields);

interface ContactValidationOptionalFields {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  address?: string;
  city?: string;
  stateCode?: string;
  postalCode?: string;
  underwriter?: string;
  suffix?: string;
  fileNumber?: string;
}

export interface ContactValidationResult {
  transactionId: string;
  transactionStatus?: TransactionStatusEnum;
  isPhoneMatch: boolean;
  phoneCarrier: string;
  phoneType: string;
  isPrepaidPhone: boolean;
  emailRegisteredDate: string;
  isPdf?: boolean;
  isAddressMatch: boolean;
  phoneMatchesSubmittedData: string;
  prepaidPhone: string;
  streetAddressMatch: string;
  risks: string[];
  submittedData?: ContactValidationSubmittedData;
  fileId?: string;
}

export enum FieldsStatus {
  PASSED = 'Passed',
  SUCCESS = 'Success',
  WARNING = 'Warning',
  FAILED = 'Failed',
  ERROR = 'Error',
  DEFAULT = 'Default',
  YES = 'Yes',
  NO = 'No',
  NOT_APPLICABLE = 'N/A',
  ISSUES_FOUND = 'Issues Found',
  EXPIRED = 'Expired',
  LIMIT_REACHED = 'Limit Reached',
  PENDING = 'Pending',
  USER_CANCELLED = 'User Cancelled',
  REQUEST_CANCELLED = 'Request Cancelled',
}

export type VerificationStatus = FieldsStatus;

export interface SummaryStatus {
  customerName: string;
  transactionDate: string;
  barcodeRecognized: VerificationStatus;
  expiredId: VerificationStatus;
  ocrBarcodeMatches: VerificationStatus;
  documentLivenessFront: VerificationStatus;
  selfie: VerificationStatus;
  deviceAddress: VerificationStatus;
  mailingAddress: VerificationStatus;
  phoneNumber: VerificationStatus;
  risks: string[];
  formatRecognized?: VerificationStatus;
  selfieLive: VerificationStatus;
  selfieMatch: VerificationStatus;
  frontMatchesBack: VerificationStatus;
  nameMatchResult?: VerificationStatus;
  nameMatchReason?: string;
}

export enum DocType {
  NORTH_AMERICAN_DRIVER_LICENSE = 'na_dl',
  PASSPORT = 'passport',
  OTHER = 'other',
  DL = 'DL',
}

export enum Language {
  ENGLISH = 'english',
  SPANISH = 'spanish',
  FRENCH = 'french',
}

export interface SubmittedData {
  firstName: string;
  fileNumber?: string;
  suffix?: string;
  lastName: string;
  phoneNumber: string;
  emailAddress: string;
  verifyWithEmail: boolean;
  middleName?: string;
  underwriter?: string;
  docType: DocType.NORTH_AMERICAN_DRIVER_LICENSE | DocType.PASSPORT | DocType.OTHER;
  language: Language.ENGLISH | Language.SPANISH | Language.FRENCH;
}

export interface IdentityVerificationResult {
  transactionId?: string;
  transactionStatus?: TransactionStatusEnum;
  submitted?: string;
  submittedData: SubmittedData;
  attempt?: number;
  isPdf?: boolean;
  summary: SummaryStatus;
  enhancedIdentityVerification?: () => void;
  fileId?: string;
}

export interface OcrData {
  similarityScore: number;
  details: string;
  similarityThreshold: number;
}

export interface IdCheckData {
  driverClass: string;
  address1: string;
  socialSecurity: string;
  processResult: string;
  eyeColor: string;
  docCategory: string;
  stateIssuerMismatch: string;
  postalCode: string;
  testCard: boolean;
  weightKilograms: string;
  uniqueID: number;
  restrictions: string;
  dLIDNumberRaw: string;
  organDonor: string;
  issueDate: string;
  isDuplicate: string;
  issuingJurisdictionAbbrv: string;
  docType: string;
  duplicateDate: string;
  issuingJurisdictionCvt: string;
  mediaType: string;
  weightPounds: string;
  heightFeetInches: string;
  age: number;
  city: string;
  hairColor: string;
  heightCentimeters: string;
  dLIDNumberFormatted: string;
  state: string;
  dateOfBirth: string;
  dateOfBirthFormatted: string;
  isRealID: string;
  endorsements: string;
  address2: string;
  transactionIdentifier: string;
  gender: string;
  firstName: string;
  middleName: string;
  expired: string;
  expirationDate: string;
  extendedResultCode: string;
  lastName: string;
}

export interface ICancelRequestPayload {
  transactionId: string;
  fileId: string;
}

export interface ICancelRequestResponse {
  transactionId: string;
  fileId: string;
  transactionStatus: TransactionStatusEnum;
  refrerenceId: string;
}

export interface OcrMatchData {
  dlRestrictionsMatchDetails: OcrData;
  documentNumberMatchDetails: OcrData;
  isIssueDateMatch: boolean;
  dlEndorsementMatchDetails: OcrData;
  dlClassMatchDetails: OcrData;
  expirationDateMatchDetails: OcrData;
  nameMatchDetails: OcrData;
  weightMatchDetails: OcrData;
  sexMatchDetails: OcrData;
  isDlClassMatch: boolean;
  issuerNameMatchDetails: OcrData;
  isEyeColorMatch: boolean;
  isDobMatch: boolean;
  dobMatchDetails: OcrData;
  isIssuerNameMatch: boolean;
  eyeColorMatchDetails: OcrData;
  isDlRestrictionsMatch: boolean;
  isNationalityMatch: boolean;
  isRealIdMatch: boolean;
  realIdMatchDetails: OcrData;
  heightMatchDetails: OcrData;
  isHeightMatch: boolean;
  isDocumentNumberMatch: boolean;
  isWeightMatch: boolean;
  isSexMatch: boolean;
  issueDateMatchDetails: OcrData;
  addressMatchDetails: OcrData;
  isNameMatch: boolean;
  isAddressMatch: boolean;
  isCountryCodeMatch: boolean;
  isDlEndorsementMatch: boolean;
  isExpirationDateMatch: boolean;
}

export interface FacialData {
  errorMessage: string;
  isLive: boolean;
  livenessProbability: number;
  livenessScore: number;
  matched: boolean;
  matchProbability: number;
  matchScore: number;
  photoFace: string;
}

export interface DocumentLivenessFrontData {
  screenReplay: { live: number; probability: number; threshold: number };
  printedCopy: { live: number; probability: number; threshold: number };
  portraitSubstitution: { live: number; probability: number; threshold: number };
  live: number;
}

export interface DocumentLivenessIdRnDData {
  front: DocumentLivenessFrontData;
}

export interface OCRData {
  weightKilograms: string;
  errorMessage: string;
  countryCode: string;
  dlClass: string;
  address: string;
  documentNumber: string;
  eyeColor: string;
  dateOfBirth: string;
  dateOfBirthFormatted: string;
  dlRestrictions: string;
  isRealID: string;
  sex: string;
  dateOfExpiry: string;
  fullDocumentImageBase64: string;
  age: string;
  lastName: string;
  nationality: string;
  height: string;
  firstName: string;
  dlEndorsement: string;
  faceImageBase64: string;
  dateOfExpiryFormatted: string;
  dateOfIssue: string;
  placeOfBirth: string;
  dateOfIssueFormatted: string;
  fullName: string;
  documentRecognized: number;
  issuerName: string;
}

export interface IPQSAssociatedEmailAddresses {
  emails: string[];
  status: string;
}

export interface IPQSPhoneData {
  mnc: string;
  leaked: boolean;
  dialing_code: number;
  active: boolean;
  accurate_country_code: boolean;
  active_status: string;
  user_activity: string;
  spammer: boolean;
  city: string;
  valid: boolean;
  local_format: string;
  country: string;
  region: string;
  risky: boolean;
  success: boolean;
  prepaid: boolean;
  request_id: string;
  recent_abuse: boolean;
  line_type: string;
  timezone: string;
  do_not_call: boolean;
  associated_email_addresses: IPQSAssociatedEmailAddresses;
  sms_domain: string;
  formatted: string;
  sms_email: string;
  VOIP: boolean;
  zip_code: string;
  fraud_score: number;
  name: string;
  mcc: string;
  message: string;
  carrier: string;
}

export interface IPQSPhoneDetails {
  data: IPQSPhoneData;
  success: boolean;
  message: string;
  result: boolean;
}

export interface IPQSAddressDistance {
  kilometers: number;
  miles: number;
}

export interface IPQSTransactionDetails {
  billing_phone_carrier: string;
  bin_type: string;
  billing_phone_country_code: string;
  valid_billing_email: boolean;
  shipping_phone_country: string;
  is_prepaid_card: boolean;
  risk_score: number;
  risky_shipping_phone: boolean;
  user_activity: string;
  shipping_phone_country_code: string;
  valid_shipping_address: boolean;
  bin_bank_name: string;
  email_name_identity_match: string;
  fraudulent_behavior: boolean;
  shipping_address_distance: IPQSAddressDistance;
  leaked_user_data: boolean;
  phone_email_identity_match: string;
  valid_shipping_phone: boolean;
  valid_shipping_email: boolean;
  billing_phone_country: string;
  shipping_phone_carrier: string;
  risky_username: boolean;
  name_address_identity_match: string;
  leaked_shipping_email: boolean;
  billing_address_distance: IPQSAddressDistance;
  shipping_phone_line_type: string;
  valid_billing_address: boolean;
  leaked_billing_email: boolean;
  risk_factors: string[];
  valid_billing_phone: boolean;
  billing_phone_line_type: string;
  phone_name_identity_match: string;
  phone_address_identity_match: string;
  risky_billing_phone: boolean;
  address_email_identity_match: string;
  bin_country: string;
}

export interface IPQSAddressData {
  transaction_details: IPQSTransactionDetails;
  success: boolean;
  high_risk_attacks: boolean;
  is_crawler: boolean;
  tor: boolean;
  bot_status: boolean;
  host: string;
  ASN: number;
  latitude: number;
  security_scanner: boolean;
  city: string;
  trusted_network: boolean;
  zip_code: string;
  recent_abuse: boolean;
  message: string;
  proxy: boolean;
  country_code: string;
  fraud_score: number;
  mobile: boolean;
  vpn: boolean;
  request_id: string;
  timezone: string;
  dynamic_connection: boolean;
  ISP: string;
  region: string;
  organization: string;
  connection_type: string;
  frequent_abuser: boolean;
  active_tor: boolean;
  longitude: number;
  shared_connection: boolean;
  abuse_velocity: string;
  active_vpn: boolean;
}

export interface IPQSAddressDetails {
  data: IPQSAddressData;
  success: boolean;
  message: string;
  result: boolean;
}

export interface IPQSFraudData {
  region: string;
  connection_type: string;
  country: string;
  is_crawler: boolean;
  tor: boolean;
  city: string;
  latitude: number;
  timezone: string;
  model: string;
  browser: string;
  ip_address: string;
  mobile: boolean;
  first_seen: string;
  isp: string;
  reasons: string[];
  longitude: number;
  operating_system: string;
  recent_abuse: boolean;
  vpn: boolean;
  device_id: string;
  organization: string;
  active_vpn: boolean;
  request_id: string;
  bot_status: boolean;
  brand: string;
  last_seen: string;
  active_tor: boolean;
  proxy: boolean;
  unique: boolean;
  fraud_chance: number;
}

export interface IPQSFraudDetails {
  data: IPQSFraudData;
  success: boolean;
  result: boolean;
  message: string;
}

export interface IPQSModule {
  addressDetails: IPQSAddressDetails;
  fraudDetails: IPQSFraudDetails;
  phoneDetails: IPQSPhoneDetails;
}

export interface ExtendedIdCheckData {
  dateOfExpiry: string;
  dateOfBirth: string;
  errorMessage: string;
  firstName: string;
  dateOfIssueFormatted: string;
  expiredStatus: string;
  documentNameBack: string;
  issuingAuthority: string;
  documentNumber: string;
  issuerName: string;
  sex: string;
  documentType: string;
  dateOfBirthFormatted: string;
  birthName: string;
  dateOfIssue: string;
  fullName: string;
  address: string;
  placeOfBirth: string;
  countryCode: string;
  nationality: string;
  documentRecognized: number;
  age: string;
  documentName: string;
  lastName: string;
  dateOfExpiryFormatted: string;
}

export interface IntelliCheckReportModules {
  idcheck: {
    data: IdCheckData;
    success: boolean;
    message: string;
    result: boolean;
  };
  ocrMatch: {
    data: OcrMatchData;
    success: boolean;
    message: string;
    result: boolean;
  };
  facial: {
    data: FacialData;
    success: boolean;
    result: boolean;
    message: string;
  };
  images: {
    front: string;
    back: string;
    selfie: string;
  };
  documentLivenessIdRnD: {
    data: DocumentLivenessIdRnDData;
    result: boolean;
    message: string;
    success: boolean;
  };
  ocr: {
    data: OCRData;
    success: boolean;
    message: string;
    result: boolean;
  };
  ipqs: IPQSModule;
  extendedId: {
    data: ExtendedIdCheckData;
    success: boolean;
    message: string;
    result: boolean;
  };
}
export interface IntelliCheckReportResponse extends IdentityVerificationResult {
  reportData: IntelliCheckReportModules;
  errors: Array<{ message: string; errorCode: string }>;
  messages: string[];
  warnings: Array<{ message: string; code: string }>;
}
export class Address {
  isForeignAddress?: boolean;
  address1?: string;
  address2?: string;
  city?: string;
  stateOrProvince?: string;
  postalCode?: string;
  county?: string;
  country?: string;
}

export type PartyPayload = Pick<
  Party,
  | 'partyType'
  | 'isSpouse'
  | 'address'
  | 'firstName'
  | 'middleName'
  | 'lastName'
  | 'suffix'
  | 'phoneNumber'
  | 'emailAddress'
> & {
  fileId: string;
  firmId: string;
  partyId: number;
  party: 'Buyer' | 'Seller' | 'Lender';
};

export interface Property {
  propertyId: number;
  isDefaultProperty?: boolean;
  propertyType: string;
  address: Address;
  apn: string;
  legalDescription: string;
  longLegalDesc: string;
  legalSection: string;
  legalDistrict: string;
  alternateAPN: string;
  taxAccountNumber: string;
  censusTractBlock: string;
  legalBlock: string;
  legalLot: string;
  townshipRangeSection: string;
  legalBookPage: string;
  mapReference: string;
  subdivision: string;
  schoolDistrict: string;
  municipalityTownship: string;
  neighborhoodName: string;
  situsCarrierRoute: string;
  taxInformation: TaxInformation;
  parsedStreetAddress: ParsedStreetAddress;
  ownerInformation: OwnerInformation;
  tract: string;
  marketArea: string;
  createdBy: string;
  createdDate: string;
  updatedBy: string;
  updatedDate: string;
  landUseDescription?: string;
  latitude?: number;
  longitude?: number;
  propertyDetailId?: number;
  combinedEstimatedLoanBalance?: number;
  risks?: Risk[];
  onFileOwnerNames?: string[];
  onFileOwnerAddressWithPartyName?: string[];
  expanded?: boolean;
}

export interface Risk {
  category: number;
  categoryName: string;
  type: number;
  typeName: string;
  severity: number;
  severityName: 'Alert' | 'Warning' | 'None' | 'Error' | 'Success' | 'Pending';
  title: string;
  description: string;
}

export interface AlertProps {
  icon?: ReactNode;
  content: ReactNode;
  action?: ReactNode;
  severity: Color;
}

export interface TaxInformation {
  taxYear?: number;
  propertyTax?: number;
  taxArea?: string;
  taxExemption?: string;
  assessedYear?: number;
  assessedValue?: number;
  landValue?: number;
  improvementValue?: number;
  improvedPercent?: number;
  marketValue?: number;
  marketLandValue?: number;
  marketImprovValue?: number;
  marketImprovValuePercent?: number;
}

export interface ParsedStreetAddress {
  standardizedHouseNumber?: number;
  standardizedHouseNumberString?: string;
  directionPrefix?: string;
  streetName?: string;
  streetSuffix?: string;
  directionSuffix?: string;
}

export interface OwnerInformation {
  ownerNames?: string;
  ownerOccupiedIndicator?: string;
  vestingOwner?: string;
  vestingOwnershipRight?: string;
  streetAddress?: string;
  city?: string;
  state?: string;
  zip9?: string;
  mailCarrierRoute?: string;
}
export interface OrderInsightJacket {
  risks: Risk[];
  authLimit: number;
  isMaxLiabilityAmount: boolean;
  fileServiceJacketId: number;
  policyNumber: string;
  policyType: string;
  liabilityAmount: number;
  policyEffectiveDate: string;
  policyStatus: string;
}

export enum RiskType {
  APNAddressMismatch,
  AuthLimit,
  ClaimParty,
  ClaimProperty,
  FinCEN,
  Foreign,
  LowAssess,
  LowMarket,
  MultipleProperties,
  NonOwner,
  OwnerName,
  OwnerAddr,
  SDN,
  Vacant,
  WatchListParty,
  WatchListProperty,
  MissingData,
  Unknown,
  Error,
}

export const severityOrder = {
  Alert: 1,
  Warning: 2,
  None: 3,
  Error: 4,
  Pending: 5,
  Success: 6,
};

export const filePartyTypeMap = {
  Buyer: 'Buyer/Borrower',
  Seller: 'Seller/Owner',
  Lender: 'Lender',
};

export type PropertyOrderInsightsProps = {
  property: Property;
  isPdf: boolean;
  index?: number;
  policyLiabilityJacket?: OrderInsightJacket;
};

export type PartyOrderInsightsProps = {
  party: Party;
  isPdf: boolean;
  index?: number;
  timezone?: string;
  enableStatus?: {
    contactValidation?: { enabled: boolean; productCode: string; description: string };
    enhancedIdentityVerification?: { enabled: boolean; productCode: string; description: string };
  };
};

export enum RiskCategory {
  AssessedValue,
  ClaimAlertParty,
  FinCen,
  MarketValue,
  MortgageBalance,
  Occupancy,
  OwnerAddr,
  OwnerName,
  PolicyLiability,
  PropertyInfo,
  SDN,
  WatchListParty,
  Unknown,
  Property = 'Property',
  Party = 'Party',
}

export const OrderInsightsError: FileOrderInsightsType = {
  fileId: '',
  fileNumber: '',
  isGenerating: false,
  hasError: true,
  parties: [],
  properties: [],
  jackets: [],
};

export const categoryToTypesMap: { [key in RiskCategory]: number[] } = {
  [RiskCategory.AssessedValue]: [RiskType.LowAssess, RiskType.MultipleProperties, RiskType.MissingData],
  [RiskCategory.ClaimAlertParty]: [RiskType.ClaimParty, RiskType.MissingData, RiskType.Error],
  [RiskCategory.FinCen]: [RiskType.FinCEN, RiskType.MissingData],
  [RiskCategory.MarketValue]: [RiskType.LowMarket, RiskType.MultipleProperties, RiskType.MissingData],
  [RiskCategory.MortgageBalance]: [RiskType.MissingData],
  [RiskCategory.Occupancy]: [RiskType.NonOwner, RiskType.MissingData],
  [RiskCategory.OwnerAddr]: [RiskType.OwnerAddr, RiskType.Foreign, RiskType.MissingData],
  [RiskCategory.OwnerName]: [RiskType.OwnerName, RiskType.MissingData],
  [RiskCategory.PropertyInfo]: [
    RiskType.APNAddressMismatch,
    RiskType.WatchListProperty,
    RiskType.ClaimProperty,
    RiskType.Vacant,
    RiskType.MissingData,
    RiskType.Error,
  ],
  [RiskCategory.SDN]: [RiskType.SDN, RiskType.MissingData, RiskType.Error],
  [RiskCategory.WatchListParty]: [RiskType.WatchListParty, RiskType.MissingData, RiskType.Error],
  [RiskCategory.Property]: [RiskType.MissingData],
  [RiskCategory.Party]: [],
  [RiskCategory.PolicyLiability]: [RiskType.AuthLimit, RiskType.MissingData],
  [RiskCategory.Unknown]: [RiskType.MissingData],
};

export function getParentSeverity(risks: Risk[] | undefined, categoryName: RiskCategory): string {
  if (!risks || risks.length === 0) {
    return 'Success';
  }

  const isPropertyOrParty = categoryName === RiskCategory.Property || categoryName === RiskCategory.Party;
  const types = categoryToTypesMap[categoryName];

  const filteredRisks = isPropertyOrParty
    ? risks.filter((x) => x.categoryName !== RiskCategory[RiskCategory.Unknown])
    : risks.filter(
        (x) =>
          x.categoryName === RiskCategory[categoryName] &&
          types.includes(RiskType[x.typeName as keyof typeof RiskType]),
      );

  const sortedRisks =
    filteredRisks.length > 1
      ? filteredRisks.sort((a, b) => severityOrder[a?.severityName] - severityOrder[b?.severityName])
      : filteredRisks;

  return sortedRisks.length > 0 ? sortedRisks[0]?.severityName : 'Success';
}

export function capitalizeFirstLetter(str: string): string {
  const words = str.split(' ');
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join(' ');
}

export function categorizeOrderInsightRisks(
  risks: Risk[] = [],
  categoryName: RiskCategory,
): { alertRisks: Risk[]; warningRisks: Risk[]; unknownRisks: Risk[]; errorRisks: Risk[] } {
  const alertRisks: Risk[] = [];
  const warningRisks: Risk[] = [];
  const unknownRisks: Risk[] = [];
  const errorRisks: Risk[] = [];

  const filteredRisks = risks.filter((x) => x.categoryName === RiskCategory[categoryName]);

  filteredRisks?.forEach((risk) => {
    switch (risk.severityName) {
      case 'Alert':
        alertRisks.push(risk);
        break;
      case 'Warning':
        warningRisks.push(risk);
        break;
      case 'None':
        unknownRisks.push(risk);
        break;
      case 'Error':
        errorRisks.push(risk);
        break;
    }
  });

  return { alertRisks, warningRisks, unknownRisks, errorRisks };
}
export function toFullAddress(addr: Address): string {
  const addressParts: string[] = [];

  if (addr.address1) {
    addressParts.push(addr.address1);
  }
  if (addr.address2) {
    addressParts.push(addr.address2);
  }

  let cityLine = '';
  if (addr.city) {
    cityLine += addr.city;
  }
  if (addr.stateOrProvince) {
    if (cityLine) {
      cityLine += ', ';
    }
    cityLine += addr.stateOrProvince;
  }
  if (addr.postalCode) {
    if (cityLine) {
      cityLine += ' ';
    }
    cityLine += addr.postalCode;
  }
  if (cityLine) {
    addressParts.push(cityLine);
  }

  if (addr.country) {
    addressParts.push(addr.country);
  }

  return addressParts.join(', ');
}

export function extractRisks(entities: (Party | Property)[], severities: string[]): Risk[] {
  let isContactValidationRiskFound = false;
  let isIdentityVerificationRiskFound = false;
  return entities.reduce((acc: Risk[], entity: Party | Property) => {
    if (entity.risks) {
      acc = acc.concat(entity.risks.filter((risk: Risk) => severities.some((x) => x === risk.severityName)));
    }

    if ((entity as Party).contactValidation?.risks?.length && !isContactValidationRiskFound) {
      isContactValidationRiskFound = true;
      acc = acc.concat(
        (entity as Party).contactValidation?.risks?.map?.((risk: string) => ({
          category: 0,
          categoryName: 'ContactValidation',
          type: 0,
          typeName: 'ContactValidation',
          severity: 1,
          severityName: 'Alert',
          title: '',
          description: risk,
        })) as Risk[],
      );
    }

    if ((entity as Party).enhancedIdentifyVerification?.summary?.risks?.length && !isIdentityVerificationRiskFound) {
      isIdentityVerificationRiskFound = true;
      acc = acc.concat(
        (entity as Party).enhancedIdentifyVerification?.summary?.risks?.map?.((risk: string) => ({
          category: 0,
          categoryName: 'IdentityVerification',
          type: 0,
          typeName: 'IdentityVerification',
          severity: 1,
          severityName: 'Alert',
          title: '',
          description: risk,
        })) as Risk[],
      );
    }
    return acc;
  }, []);
}

export const disclaimerText =
  "First American Order Insights is a service offered exclusively to First American’s policy issuing agents for the purpose of aiding in the preparation of title policies underwritten by First American Title Insurance Company and its affiliated insurer entities. This service utilizes agent-provided data, alongside First American's internal data and data from third-party resources, to identify potential matters of concern or property record mismatches signaling the necessity for further investigation and security measures. Please be aware that the data presented in Order Insights is purely informational and requires verification by the issuing Agent, as no assurance can be given regarding the accuracy of the internal data or third-party information provided through Order Insights. Additionally, it is important to recognize that many of the parties and properties reported may be victims of identity theft or other forms of fraud. The inclusion of persons or properties in Order Insight lists is not an indication that the person or property was involved in disreputable activities but serves as a warning that extra vigilance is essential to ensure the protection of all transaction participants.";

export const onFileOwnerAddresses = (parties: Party[], filePartyType: string): string[] => {
  return parties
    .filter((party) => party.filePartyType === filePartyType)
    .map((party) => {
      const address = toFullAddress(party.address);
      return address ? `${address} (${party.partyName})` : '';
    })
    .filter((address) => address !== '');
};

export const onFileOwnerNames = (parties: Party[], filePartyType: string): string[] => {
  return parties
    .filter((party) => party.filePartyType === filePartyType)
    .map((party) => {
      return party.partyName;
    });
};

export enum IdentitiVerificationStatus {
  Pending = 'Pending',
  Completed = 'Completed',
  Failed = 'Failed',
}
